import {
  Paper,
  Box,
  Typography,
  Grid,
  FormControl,
  Select,
  MenuItem,
  TextField,
} from '@mui/material';
import {
  Grading as GradingIcon,
} from '@mui/icons-material';
import type { Application } from '../../../types/application';
import { FormData } from '../types';
import { GRADE_LIST } from '../constants';
import { APPLICATION_STATUS_LIST } from '../../../../../constants/statusList';
import { APPLICATION_STATUS_COLORS } from '../../../../../constants/statusColors';
import { calculateTotalPrices } from '../../../../../utils/applicationStatusCalculator';

interface Props {
  application: Application;
  formData: FormData;
  onFormDataChange: (data: Partial<FormData>) => void;
}

const ProcessingForm = ({ application, formData, onFormDataChange }: Props) => {
  const handleChange = (field: keyof FormData) => (e: any) => {
    onFormDataChange({ [field]: e.target.value });
  };

  // 🔢 하위 기기들의 총 금액 계산
  const { total_price_offer, total_final_price } = calculateTotalPrices(application.devices || []);

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 3,
        p: 2.5,
        borderRadius: 3,
        background: 'linear-gradient(135deg, #fff 0%, #f8fafc 100%)',
        border: '2px solid #1976d2',
        boxShadow: '0 4px 12px rgba(25, 118, 210, 0.1)',
      }}
    >
      <Typography variant="subtitle2" fontWeight={600} color="#1976d2" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <GradingIcon sx={{ mr: 1, fontSize: 18 }} />
        처리 정보
      </Typography>
      
      <Grid container spacing={2}>
        {/* 상태 변경 */}
        <Grid item xs={6}>
          <FormControl fullWidth size="small">
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500 }}>
              상태
            </Typography>
            <Select
              value={formData.status}
              onChange={handleChange('status')}
              sx={{ borderRadius: 2, bgcolor: '#f8fafc' }}
            >
              {APPLICATION_STATUS_LIST.filter(status => status !== '전체').map((status) => (
                <MenuItem key={status} value={status}>
                  <Box display="flex" alignItems="center">
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: APPLICATION_STATUS_COLORS[status] || '#757575',
                        mr: 1,
                      }}
                    />
                    {status}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* 등급 */}
        <Grid item xs={6}>
          <FormControl fullWidth size="small">
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500 }}>
              등급
            </Typography>
            <Select
              value={formData.grade}
              onChange={handleChange('grade')}
              sx={{ borderRadius: 2, bgcolor: '#f8fafc' }}
            >
              {GRADE_LIST.map((grade) => (
                <MenuItem key={grade.value} value={grade.value}>
                  <Box display="flex" alignItems="center">
                    <Typography sx={{ mr: 0.5 }}>{grade.icon}</Typography>
                    <Typography color={grade.color} fontWeight={600}>
                      {grade.label}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* 총 검수금액 (읽기 전용) */}
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500, display: 'block' }}>
            총 검수금액
          </Typography>
          <TextField
            size="small"
            fullWidth
            value={total_price_offer ? total_price_offer.toLocaleString() : '0'}
            InputProps={{
              startAdornment: <Typography color="text.secondary" sx={{ mr: 1 }}>₩</Typography>,
              readOnly: true,
            }}
            sx={{ 
              '& .MuiOutlinedInput-root': { 
                borderRadius: 2, 
                bgcolor: '#f0f9ff',
                '& input': { 
                  color: '#1976d2',
                  fontWeight: 600
                }
              } 
            }}
            placeholder="기기별 검수 가격의 총합"
          />
        </Grid>

        {/* 총 최종금액 (읽기 전용) */}
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500, display: 'block' }}>
            총 최종금액
          </Typography>
          <TextField
            size="small"
            fullWidth
            value={total_final_price ? total_final_price.toLocaleString() : '0'}
            InputProps={{
              startAdornment: <Typography color="text.secondary" sx={{ mr: 1 }}>₩</Typography>,
              readOnly: true,
            }}
            sx={{ 
              '& .MuiOutlinedInput-root': { 
                borderRadius: 2, 
                bgcolor: '#f0fff4',
                '& input': { 
                  color: '#16a34a',
                  fontWeight: 600
                }
              } 
            }}
            placeholder="기기별 최종 가격의 총합"
          />
        </Grid>

        {/* 운송장 번호 */}
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500, display: 'block' }}>
            운송장 번호
          </Typography>
          <TextField
            size="small"
            fullWidth
            value={formData.trackingNumber}
            onChange={handleChange('trackingNumber')}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f8fafc' } }}
            placeholder="운송장 번호 입력"
          />
        </Grid>

        {/* 매장 코드 */}
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500, display: 'block' }}>
            매장 코드
          </Typography>
          <TextField
            size="small"
            fullWidth
            value={formData.storeCode}
            onChange={handleChange('storeCode')}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f8fafc' } }}
            placeholder="매장 코드 입력"
          />
        </Grid>

        {/* 차감 사유 (모든 등급에서 선택적 입력 가능) */}
        <Grid item xs={12}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500, display: 'block' }}>
            차감 사유 (선택)
          </Typography>
          <TextField
            size="small"
            fullWidth
            multiline
            rows={2}
            value={formData.deductionReason}
            onChange={handleChange('deductionReason')}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f8fafc' } }}
            placeholder="차감 사유가 있으면 입력하세요"
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

export default ProcessingForm; 